import React, { useMemo } from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { ErpOrderStatus } from '@recurrency/core-api-schema/dist/common/enums';
import { VendorDTO } from '@recurrency/core-api-schema/dist/vendors/getVendor';
import moment from 'moment';
import { theme } from 'theme';

import { MetricsCard } from 'pages/purchasing/PurchasingDashboardPage/ExecutiveMetricsPage/MetricsCard';
import { InventoryBarChartBar } from 'pages/purchasing/PurchasingDashboardPage/InventoryBarChart';

import { Typography } from 'components/Typography';

import { useCoreApi } from 'hooks/useApi';

import { formatNumber } from 'utils/formatting';

import { NumberBarChart } from './NumberBarChart';

export const LeadTimeBarChart = ({ vendorReportingData }: { vendorReportingData: VendorDTO }) => {
  const oneYearAgo = useMemo(() => moment().startOf('day').subtract(1, 'year').toISOString(), []);
  const { data: leadTimeHistory, isLoading: isLeadTimeHistoryLoading } = useCoreApi(
    schemas.purchasing.getLeadTimeHistory,
    {
      queryParams: {
        filter: { vendorId: vendorReportingData.vendorId, orderDateFrom: oneYearAgo },
        limit: 1000,
      },
    },
  );

  const { data: openPOData, isLoading: isOpenPOLoading } = useCoreApi(schemas.purchaseOrders.getPurchaseOrdersV2, {
    queryParams: {
      filter: { vendorId: vendorReportingData.vendorId, status: ErpOrderStatus.Open },
      limit: 1,
      offset: 0,
    },
  });

  const leadTimes = useMemo(() => leadTimeHistory?.items?.map((item) => item.leadTime) || [], [leadTimeHistory]);
  const openPOCount = openPOData?.totalCount ?? 0;
  const percentiles = {
    p25: vendorReportingData.leadTimeP25,
    p50: vendorReportingData.leadTimeP50,
    p75: vendorReportingData.leadTimeP75,
    p90: vendorReportingData.leadTimeP90,
  };

  // Calculate x-axis domain and ticks for continuous range
  const maxLeadTime = leadTimes.length > 0 ? Math.max(...leadTimes) : 100;

  const chartData: { number: number; [key: string]: number }[] = useMemo(() => {
    const counts = leadTimes.reduce((acc, lt) => {
      acc[lt] = (acc[lt] || 0) + 1;
      return acc;
    }, {} as Record<number, number>);

    // Create array with all numbers from 0 to maxLeadTime, filling in 0s for missing values
    const result = [];
    for (let i = 0; i <= maxLeadTime; i++) {
      result.push({ number: i, count: counts[i] || 0 });
    }

    return result;
  }, [leadTimes, maxLeadTime]);

  const bars: InventoryBarChartBar[] = [
    {
      key: 'count',
      color: theme.colors.primary[500],
      title: 'Orders',
      valueFormatter: formatNumber,
    },
  ];

  const referenceLines = [
    {
      x: percentiles.p25,
      stroke: theme.colors.neutral[400],
      strokeDasharray: '5 5',
    },
    {
      x: percentiles.p50,
      stroke: theme.colors.malibu[600],
      strokeWidth: 2,
    },
    {
      x: percentiles.p75,
      stroke: theme.colors.info[500],
      strokeDasharray: '5 5',
    },
    {
      x: percentiles.p90,
      stroke: theme.colors.twilight[700],
      strokeDasharray: '5 5',
    },
  ];

  const legend = (
    <div
      className={css`
        border: 1px solid ${theme.colors.neutral[300]};
        border-radius: 4px;
        background-color: ${theme.colors.neutral[50]};
        padding: 8px 12px;
        display: inline-block;
      `}
    >
      {[
        {
          color: theme.colors.neutral[400],
          strokeDasharray: '5 5',
          label: `P25: 25% of receipts ≤ ${percentiles.p25} days `,
        },
        { color: theme.colors.malibu[600], label: `P50: 50% of receipts ≤ ${percentiles.p50} days` },
        {
          color: theme.colors.info[500],
          strokeDasharray: '5 5',
          label: `P75: 75% of receipts ≤ ${percentiles.p75} days`,
        },
        {
          color: theme.colors.twilight[700],
          strokeDasharray: '8 4',
          label: `P90: 90% of receipts ≤ ${percentiles.p90} days`,
        },
      ].map((item, index) => (
        <div
          key={index}
          className={css`
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: ${index < 3 ? '4px' : '0'};
          `}
        >
          <div
            className={css`
              width: 20px;
              height: 2px;
              background-color: ${item.color};
              ${item.strokeDasharray === '5 5'
                ? `
                background: repeating-linear-gradient(
                  to right,
                  ${item.color} 0px,
                  ${item.color} 5px,
                  transparent 5px,
                  transparent 10px
                );
              `
                : item.strokeDasharray === '8 4'
                ? `
                background: repeating-linear-gradient(
                  to right,
                  ${item.color} 0px,
                  ${item.color} 2px,
                  transparent 2px,
                  transparent 4px
                );
              `
                : ''}
            `}
          />
          <Typography style={{ fontSize: '12px', color: theme.colors.neutral[700] }}>{item.label}</Typography>
        </div>
      ))}
    </div>
  );

  return (
    <MetricsCard
      title="Lead Time History"
      description={`Average lead time for PO receipts excluding ${openPOCount} open POs.`}
      loading={isLeadTimeHistoryLoading || isOpenPOLoading}
    >
      <NumberBarChart
        data={chartData}
        bars={bars}
        valueFormatter={formatNumber}
        customXAxisProps={{
          type: 'number',
          scale: 'linear',
          domain: [0, Math.ceil(maxLeadTime / 10) * 10],
          interval: 0,
          ticks: Array.from({ length: Math.floor((Math.ceil(maxLeadTime / 10) * 10) / 10) + 1 }, (_, i) => i * 10),
          tickFormatter: (value) => `${value}`,
          label: {
            value: 'Lead Time (days)',
            position: 'insideBottom',
            offset: -10,
          },
        }}
        customYAxisProps={{
          tickCount: 4,
          interval: 0,
          tickFormatter: (value) => formatNumber(value),
          label: {
            value: 'Frequency (# PO receipts)',
            angle: -90,
            position: 'insideLeft',
            style: { textAnchor: 'middle' },
          },
        }}
        tooltipTitle={(payload: { number: number; count: number }) => `Lead Time: ${payload.number} days`}
        referenceLines={referenceLines}
        legend={legend}
      />
    </MetricsCard>
  );
};
